import { apiCreatePosExpense, apiSaveDailyReport, apiSaveSale } from "./authApi";
import { isNetworkUnavailable, readPendingSyncQueue, readPersistedJson, removePendingSyncItem, writePersistedJson } from "./offlineErp";
import { toast } from "./toast";

// Fila de sincronizacao de vendas/despesas/fecho de caixa feitos offline no
// POS. Extraido para um modulo partilhado para que a mesma logica possa ser
// acionada tanto pela pagina do POS Terminal (enquanto aberta) como por um
// efeito global em erpContext.js (sempre montado durante a sessao), evitando
// que vendas pendentes fiquem por sincronizar so porque o utilizador navegou
// para outra pagina antes da ligacao voltar.
export const POS_OFFLINE_SYNC_INTERVAL_MS = 20_000;
export const POS_OFFLINE_SYNC_BATCH_SIZE = 5;
export const POS_OFFLINE_SYNC_MAX_BACKOFF_MS = 5 * 60_000;
export const POS_OFFLINE_SYNC_TYPES = new Set(["saveSale", "saveSaleDraft", "saveDailyReport", "createPosExpense"]);

const POS_OFFLINE_SYNC_LOCK_KEY = "angolabi.erp.offline.pos.sync.lock";
const POS_OFFLINE_SYNC_LOCK_TTL_MS = 15_000;

function isOfflineSessionToken(token) {
  return String(token || "").trim() === "offline-session";
}

function tryAcquireLock(ownerId) {
  if (typeof window === "undefined") {
    return true;
  }

  const now = Date.now();

  try {
    const raw = window.localStorage.getItem(POS_OFFLINE_SYNC_LOCK_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      const expiresAt = Number(parsed?.expiresAt || 0);
      const owner = String(parsed?.owner || "");
      if (owner && owner !== ownerId && expiresAt > now) {
        return false;
      }
    }

    window.localStorage.setItem(POS_OFFLINE_SYNC_LOCK_KEY, JSON.stringify({
      owner: ownerId,
      expiresAt: now + POS_OFFLINE_SYNC_LOCK_TTL_MS,
    }));

    return true;
  } catch {
    return true;
  }
}

function releaseLock(ownerId) {
  if (typeof window === "undefined") {
    return;
  }

  try {
    const raw = window.localStorage.getItem(POS_OFFLINE_SYNC_LOCK_KEY);
    if (!raw) {
      return;
    }

    const parsed = JSON.parse(raw);
    if (String(parsed?.owner || "") === ownerId) {
      window.localStorage.removeItem(POS_OFFLINE_SYNC_LOCK_KEY);
    }
  } catch {
    // Ignora erros de lock em storage local para manter a sincronizacao resiliente.
  }
}

/**
 * Cria um "runner" com o seu proprio estado de in-flight/backoff, mas que
 * partilha o lock de localStorage com qualquer outro runner ativo (outra
 * pagina, outra aba) para nunca processar a mesma fila em duplicado.
 */
export function createPosOfflineSyncRunner(ownerId) {
  let inFlight = false;
  let backoffUntil = 0;
  let backoffMs = POS_OFFLINE_SYNC_INTERVAL_MS;

  return async function run({ authToken, companyUuid, onLicenseRestricted }) {
    if (!authToken || isOfflineSessionToken(authToken) || !companyUuid) {
      return;
    }

    if (inFlight) {
      return;
    }

    if (Date.now() < backoffUntil) {
      return;
    }

    if (!tryAcquireLock(ownerId)) {
      return;
    }

    const queue = readPendingSyncQueue();
    if (!Array.isArray(queue) || queue.length === 0) {
      releaseLock(ownerId);
      return;
    }

    const pendingItems = queue.filter((item) => {
      const type = String(item?.type || "").trim();
      if (!POS_OFFLINE_SYNC_TYPES.has(type)) {
        return false;
      }

      const itemCompanyUuid = String(item?.companyUuid || "").trim();
      return itemCompanyUuid === "" || itemCompanyUuid === companyUuid;
    });

    if (pendingItems.length === 0) {
      releaseLock(ownerId);
      return;
    }

    inFlight = true;

    try {
      let hasRetriableFailure = false;

      for (const pendingItem of pendingItems.slice(0, POS_OFFLINE_SYNC_BATCH_SIZE)) {
        if (!pendingItem?.id) {
          continue;
        }

        const payload = pendingItem?.payload;
        if (!payload || typeof payload !== "object") {
          removePendingSyncItem(pendingItem.id);
          continue;
        }

        try {
          const type = String(pendingItem?.type || "").trim();

          if (type === "saveDailyReport") {
            const savedReport = await apiSaveDailyReport(authToken, payload);
            const cacheKey = String(pendingItem?.dailyReportCacheKey || "").trim();
            if (cacheKey) {
              await writePersistedJson(cacheKey, {
                cachedAt: Date.now(),
                item: savedReport || payload,
              });
            }
          } else if (type === "createPosExpense") {
            const savedExpense = await apiCreatePosExpense(authToken, payload);
            const cacheKey = String(pendingItem?.expenseCacheKey || "").trim();
            const dateCacheKey = String(pendingItem?.expenseDateCacheKey || "").trim();
            const pendingExpenseId = String(pendingItem?.pendingExpenseId || "").trim();

            if (cacheKey) {
              const cached = await readPersistedJson(cacheKey, null);
              const cachedItems = Array.isArray(cached?.items) ? cached.items : [];
              const nextItems = cachedItems.map((item) => {
                if (!pendingExpenseId || String(item?.uuid_despesa || "").trim() !== pendingExpenseId) {
                  return item;
                }
                return savedExpense || item;
              });

              await writePersistedJson(cacheKey, {
                items: nextItems,
                cachedAt: Date.now(),
              });
            }

            if (dateCacheKey) {
              const dateCached = await readPersistedJson(dateCacheKey, null);
              const dateCachedItems = Array.isArray(dateCached?.items) ? dateCached.items : [];
              const nextDateItems = dateCachedItems.map((item) => {
                if (!pendingExpenseId || String(item?.uuid_despesa || "").trim() !== pendingExpenseId) {
                  return item;
                }
                return savedExpense || item;
              });

              await writePersistedJson(dateCacheKey, {
                items: nextDateItems,
                cachedAt: Date.now(),
              });
            }
          } else {
            await apiSaveSale(authToken, payload);
          }

          removePendingSyncItem(pendingItem.id);
          backoffMs = POS_OFFLINE_SYNC_INTERVAL_MS;
          backoffUntil = 0;
        } catch (error) {
          if (isNetworkUnavailable(error)) {
            hasRetriableFailure = true;
            break;
          }

          const statusCode = Number(error?.response?.status || 0);
          const apiCode = String(error?.apiCode || error?.response?.data?.code || "").trim();

          if (apiCode === "POS_DOCUMENT_LICENSE_RESTRICTED") {
            removePendingSyncItem(pendingItem.id);
            const fallbackDocumentCode = payload?.tipo_documento || payload?.tipoDocumento || "";
            if (typeof onLicenseRestricted === "function") {
              onLicenseRestricted(error, fallbackDocumentCode);
            } else {
              toast.warning("Uma venda offline foi bloqueada por licença durante a sincronização.", { title: "POS", duration: 4600 });
            }
            continue;
          }

          if (statusCode === 401 || statusCode === 403) {
            hasRetriableFailure = true;
            break;
          }

          if (statusCode >= 400 && statusCode < 500) {
            removePendingSyncItem(pendingItem.id);
            continue;
          }

          hasRetriableFailure = true;
          break;
        }
      }

      if (hasRetriableFailure) {
        const currentBackoff = Math.max(POS_OFFLINE_SYNC_INTERVAL_MS, Number(backoffMs || POS_OFFLINE_SYNC_INTERVAL_MS));
        backoffMs = Math.min(POS_OFFLINE_SYNC_MAX_BACKOFF_MS, currentBackoff * 2);
        backoffUntil = Date.now() + backoffMs;
      }
    } finally {
      inFlight = false;
      releaseLock(ownerId);
    }
  };
}
